// src/products/dataroom/DataroomApp.js
// Main Dataroom application - Updated to pass projects to header
import React from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
// import { useDataroomOptimized } from './hooks/useDataroomOptimized';
import { useDataroomApp } from './hooks/useDataroomApp';
// import { useGlobalStore } from '../../state/globalStore'; // No longer needed
import { findProjectBySlugOrId } from './utils/slugUtils';

// Import Components
import DataroomSidebar from './components/DataroomSidebar';
import UserMenu from '../../components/common/UserMenu';
import DataroomHomePage from './components/DataroomHomePage';
import DataroomExplorer from './components/DataroomExplorer';
import GroupManagement from './components/GroupManagement';
import SettingsPage from './pages/SettingsPage';
import BookmarksPage from './pages/BookmarksPage';
import DataroomQuestionsPage from './pages/DataroomQuestionsPage';
import PartiesPage from './pages/PartiesPage';
import TeamsPage from './pages/TeamsPage';
import UsersPage from './pages/UsersPage';
import PermissionsPage from './pages/PermissionsPage';
import WizardPage from './pages/WizardPage';
import DataroomUserActivityPage from './pages/DataroomUserActivityPage';

// Import Modals
import ProjectCreationModal from './components/Modals/ProjectCreationModal';
import BulkUserInviteModal from './components/Modals/BulkUserInviteModal';
import TemplateModal from './components/Modals/TemplateModal';
import ProjectColorModal from './components/Modals/ProjectColorModal';
import InviteModal from '../userInvites/InviteModal';

// Wrapper component to extract projectId from URL params (supports slugs or IDs)
const DataroomExplorerWrapper = (props) => {
  const { projectId: urlIdentifier } = useParams();
  const { projects, isLoading } = props;
  const [isResolving, setIsResolving] = React.useState(true);
  const [resolvedProjectId, setResolvedProjectId] = React.useState(null);
  
  React.useEffect(() => {
    if (!urlIdentifier) {
      setResolvedProjectId(null);
      setIsResolving(false);
      return;
    }
    
    // Try to parse as number first (backward compatibility)
    const numericId = parseInt(urlIdentifier, 10);
    if (!isNaN(numericId) && numericId > 0) {
      setResolvedProjectId(numericId);
      setIsResolving(false);
      return;
    }
    
    // If projects are still loading, wait
    if (isLoading) {
      return;
    }
    
    // If we have projects, try to find by slug
    if (projects && projects.length > 0) {
      const project = findProjectBySlugOrId(projects, urlIdentifier);
      if (project) {
        setResolvedProjectId(project.id);
        setIsResolving(false);
        return;
      }
    }
    
    // If projects are loaded but project not found, it's invalid
    if (!isLoading) {
      if (projects && projects.length > 0) {
        // Projects loaded but not found - log for debugging
        console.error('Project not found:', urlIdentifier, 'Available projects:', projects.map(p => ({ 
          id: p.id, 
          name: p.project_name,
          url_slug: p.url_slug, 
          url_token: p.url_token,
          full_url: p.url_slug && p.url_token ? `${p.url_slug}-${p.url_token}` : null
        })));
      }
      setIsResolving(false);
    }
  }, [urlIdentifier, projects, isLoading]);
  
  // Show loading state while resolving
  if (isResolving) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }
  
  // If identifier is invalid, redirect to home
  if (urlIdentifier && !resolvedProjectId) {
    console.error('Invalid project identifier:', urlIdentifier);
    window.location.href = '/dealroom';
    return null;
  }
  
  return (
    <DataroomExplorer
      {...props}
      projectId={resolvedProjectId}
    />
  );
};

// Generic wrapper for project routes
const ProjectRouteWrapper = ({ children, projects }) => {
  const { projectId: urlProjectId } = useParams();
  const projectId = urlProjectId ? parseInt(urlProjectId, 10) : null;
  
  // Find the project from the projects list
  const project = projects.find(p => p.id === projectId);
  
  return React.cloneElement(children, { 
    projectId,
    selectedProject: project 
  });
};

const DataroomApp = () => {
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = React.useState(false);
  const {
    user,
    userProfile,
    projects,
    selectedProjectId,
    selectedProject,
    isLoading,
    messages,
    userRole,
    canCreate,
    modals,
    breadcrumb,
    searchTerm,
    showMessage,
    handleCreateProject,
    handleProjectSelect,
    setModals,
    // Direct store actions for compatibility
    openModal,
    closeModal,
    addMessage,
    loadProjects,
    setBreadcrumb,
    setSearchTerm,
  } = useDataroomApp();

  // Compatibility variables
  const productRole = userRole; // Map userRole to productRole for compatibility

  const handleOpenInviteModal = () => setShowInviteModal(true);
  const handleCloseInviteModal = () => setShowInviteModal(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <DataroomSidebar
        userRole={userRole}
        onOpenInviteModal={handleOpenInviteModal}
        user={user}
        userProfile={userProfile}
        selectedProjectId={selectedProjectId}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        {/* Header - Now includes project selector */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              {/* Project Selector */}
              <div className="flex items-center space-x-4">
                {projects && projects.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">
                      Active Project:
                    </label>
                    <select
                      value={selectedProjectId || ''}
                      onChange={(e) => {
                        const projectId = e.target.value ? parseInt(e.target.value, 10) : null;
                        if (projectId) {
                          handleProjectSelect(projectId);
                        }
                      }}
                      className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        selectedProjectId 
                          ? 'border-blue-500 bg-blue-50 text-blue-900 font-medium' 
                          : 'border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      <option value="">Select Dealroom</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.project_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="flex items-center space-x-4">
                {user && (
                  <UserMenu 
                    user={user}
                    userProfile={userProfile}
                    userRole={userRole}
                  />
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Messages */}
        {messages.length > 0 && (
          <div className="fixed top-4 right-4 z-50 space-y-2" style={{ maxWidth: '400px' }}>
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`px-4 py-3 rounded-lg shadow-lg ${
                  msg.type === 'error' ? 'bg-red-500 text-white' :
                  msg.type === 'success' ? 'bg-green-500 text-white' :
                  'bg-blue-500 text-white'
                }`}
              >
                {msg.text}
              </div>
            ))}
          </div>
        )}

        {/* Routes */}
        <div className="flex-1 overflow-y-auto p-6">
          <Routes>
            <Route
              path="/"
              element={
                <DataroomHomePage
                  user={user}
                  projects={projects}
                  onProjectClick={handleProjectSelect}
                  onCreateProject={() => openModal('showTemplateModal')}
                  canCreate={canCreate} // This is now correct for the homepage
                  userRole={productRole} // Pass the general product role
                />
              }
            />
            <Route
              path="/questions"
              element={
                <DataroomQuestionsPage
                  projectId={selectedProjectId}
                  projectName={selectedProject?.project_name}
                  user={user}
                  userRole={userRole}
                  projects={projects}
                  onMessage={addMessage}
                />
              }
            />
            <Route
              path="/groups"
              element={
                <GroupManagement
                  userRole={userRole}
                  onMessage={addMessage}
                />
              }
            />
            <Route
              path="/users"
              element={
                <UsersPage
                  userRole={userRole}
                  onMessage={showMessage}
                />
              }
            />
        <Route
          path="/parties"
          element={
            <PartiesPage
              userRole={userRole}
              onMessage={showMessage}
            />
          }
        />
        <Route
          path="/teams"
          element={
            <TeamsPage
              userRole={userRole}
              onMessage={showMessage}
            />
          }
        />
        <Route
          path="/permissions"
          element={
            <PermissionsPage
              userRole={userRole}
              onMessage={showMessage}
            />
          }
        />
        <Route
          path="/wizard/:projectId"
          element={
            <WizardPage
              projects={projects}
              onMessage={showMessage}
            />
          }
        />
            <Route
              path="/:projectId"
              element={
                <DataroomExplorerWrapper
                  projectId={selectedProjectId}
                  projectName={selectedProject?.project_name}
                  user={user}
                  userRole={userRole}
                  projects={projects}
                  isLoading={isLoading}
                  selectedProjectId={selectedProjectId}
                  onProjectSelect={handleProjectSelect}
                  onMessage={addMessage}
                  onCreateQuestion={(context) => {
                    // Navigate to questions page instead of opening modal to avoid infinite loop
                    navigate('/dealroom/questions');
                    addMessage('Navigate to Dealroom Questions tab to ask a question', 'info');
                  }}
                  openModal={openModal}
                  closeModal={closeModal}
                  modals={modals}
                  onProjectColorChanged={loadProjects}
                  searchTerm={searchTerm}
                  breadcrumb={breadcrumb}
                  onSetBreadcrumb={setBreadcrumb}
                  onSetSearchTerm={setSearchTerm}
                />
              }
            />
            <Route
              path="/:projectId/parties"
              element={
                <ProjectRouteWrapper projects={projects}>
                  <GroupManagement
                    userRole={userRole}
                    onMessage={addMessage}
                  />
                </ProjectRouteWrapper>
              }
            />
            <Route
              path="/:projectId/settings"
              element={
                <ProjectRouteWrapper projects={projects}>
                  <SettingsPage
                    projectId={selectedProjectId}
                    selectedProject={selectedProject}
                    onMessage={addMessage}
                    onProjectUpdate={loadProjects}
                    onProjectColorChanged={loadProjects}
                  />
                </ProjectRouteWrapper>
              }
            />
            <Route
              path="/:projectId/bookmarks"
              element={
                <ProjectRouteWrapper projects={projects}>
                  <BookmarksPage
                    user={user}
                    projectId={selectedProjectId}
                    selectedProject={selectedProject}
                    onMessage={addMessage}
                  />
                </ProjectRouteWrapper>
              }
            />
            <Route
              path="/:projectId/activity"
              element={
                <ProjectRouteWrapper projects={projects}>
                  <DataroomUserActivityPage
                    projectId={selectedProjectId}
                    projectName={selectedProject?.project_name}
                    userRole={userRole}
                  />
                </ProjectRouteWrapper>
              }
            />
            <Route
              path="/bookmarks"
              element={
                <BookmarksPage
                  user={user}
                  projectId={selectedProjectId}
                  selectedProject={selectedProject}
                  onMessage={addMessage}
                />
              }
            />
          </Routes>
        </div>
      </div>

      {/* Modals */}
      {modals.showTemplateModal && (
        <TemplateModal
          isOpen={modals.showTemplateModal}
          onClose={() => closeModal('showTemplateModal')}
          onSelectTemplate={(template) => {
            setModals(prev => ({
              ...prev,
              selectedTemplate: template,
              showTemplateModal: false,
              showProjectCreation: true
            }));
          }}
        />
      )}

      {modals.showProjectCreation && (
        <ProjectCreationModal
          isOpen={modals.showProjectCreation}
          onClose={() => {
            closeModal('showProjectCreation');
            setModals(prev => ({ ...prev, selectedTemplate: null }));
          }}
          onProjectCreated={(name, description, template, urlName) => {
            handleCreateProject({
              name,
              description,
              color: template?.color || '#3B82F6',
              urlName
            });
          }}
          selectedTemplate={modals.selectedTemplate}
        />
      )}

      {modals.showBulkInvite && (
        <BulkUserInviteModal
          isOpen={modals.showBulkInvite}
          onClose={() => closeModal('showBulkInvite')}
          projectId={selectedProjectId}
          onSuccess={() => {
            closeModal('showBulkInvite');
            addMessage('Users invited successfully', 'success');
          }}
        />
      )}

      {modals.projectColor && (
        <ProjectColorModal
          isOpen={!!modals.projectColor}
          onClose={() => closeModal('projectColor')}
          project={modals.projectColor}
          onColorChanged={(newColor) => {
            loadProjects();
            closeModal('projectColor');
            addMessage('Project color updated', 'success');
          }}
        />
      )}

      {/* Questions Modal - Removed as it has internal isOpen state and causes loops */}

      {showInviteModal && (
        <InviteModal
          isOpen={showInviteModal}
          onClose={handleCloseInviteModal}
          inviter={user}
          context={{ product: 'dataroom', projectid: selectedProjectId }}
        />
      )}
    </div>
  );
};

export default DataroomApp;